import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { C, S } from '../../../utils/styles';
import Icon from '../../Icon';
import { useToast } from '../../Toast';
import { useClients } from '../../../context/ClientsContext';
import { useUsers } from '../../../context/UsersContext';
import { renderMarkdown } from '../learning/markdown';
import {
  BULK_AUDIENCES,
  listRecipients,
  bulkSend,
  audienceLabel,
} from './bulkEmailApi';

// ------------------------------------------------------------------
// Bulk Email — Compose panel
// ------------------------------------------------------------------

export default function BulkEmailCompose({ onSent }) {
  const showToast = useToast();
  const { clients } = useClients();
  const { currentUser, isSuperUser } = useUsers();
  const isSuper = !!isSuperUser?.();

  const [audience, setAudience] = useState('employee');
  const [scope, setScope] = useState('all');               // 'all' | 'client' | 'explicit'
  const [clientId, setClientId] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]); // BulkEmailRecipient[]
  const [recipientPool, setRecipientPool] = useState([]);  // full available pool for current audience+client
  const [poolLoading, setPoolLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Filter the audience radios: AMs cannot send to "account_manager"
  const audienceOptions = useMemo(() => {
    return BULK_AUDIENCES.filter((a) => !(a.value === 'account_manager' && !isSuper));
  }, [isSuper]);

  // Load the recipient pool whenever audience/clientId changes
  useEffect(() => {
    let mounted = true;
    setPoolLoading(true);
    listRecipients({
      audience,
      clientId: audience !== 'account_manager' && clientId ? clientId : undefined,
    })
      .then((d) => {
        if (!mounted) return;
        setRecipientPool(d.recipients || []);
        setSelectedRecipients([]);
      })
      .catch((err) => {
        if (!mounted) return;
        setRecipientPool([]);
        showToast(err.message || 'Failed to load recipients', 'error');
      })
      .finally(() => { if (mounted) setPoolLoading(false); });
    return () => { mounted = false; };
  }, [audience, clientId, showToast]);

  // Decide effective recipients based on scope
  const effectiveRecipients = useMemo(() => {
    if (scope === 'explicit') return selectedRecipients;
    if (scope === 'client') {
      // recipientPool was loaded with clientId already → all of it
      return recipientPool;
    }
    return recipientPool; // 'all'
  }, [scope, selectedRecipients, recipientPool]);

  // Search filter applied only to the picker, not the actual send
  const filteredPool = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipientPool;
    return recipientPool.filter((r) =>
      (r.email || '').toLowerCase().includes(q) ||
      (r.name || '').toLowerCase().includes(q) ||
      (r.clientName || '').toLowerCase().includes(q)
    );
  }, [recipientPool, search]);

  const toggleRecipient = (r) => {
    setSelectedRecipients((prev) => {
      const exists = prev.find((x) => x.email === r.email);
      if (exists) return prev.filter((x) => x.email !== r.email);
      return [...prev, r];
    });
  };

  const selectAllFiltered = () => {
    const map = new Map(selectedRecipients.map((r) => [r.email, r]));
    for (const r of filteredPool) map.set(r.email, r);
    setSelectedRecipients(Array.from(map.values()));
  };
  const clearSelection = () => setSelectedRecipients([]);

  const validate = () => {
    if (!subject.trim()) { showToast('Subject is required', 'error'); return false; }
    if (!body.trim()) { showToast('Body is required', 'error'); return false; }
    if (effectiveRecipients.length === 0 && scope !== 'all') {
      showToast('Please pick at least one recipient', 'error');
      return false;
    }
    if (scope === 'client' && !clientId) {
      showToast('Please pick a client', 'error');
      return false;
    }
    return true;
  };

  const handleSendTest = async () => {
    if (!subject.trim() || !body.trim()) {
      showToast('Add a subject and body first', 'error');
      return;
    }
    setSending(true);
    try {
      const res = await bulkSend({
        audience,
        subject: subject.trim(),
        body,
        scope: 'all',
        recipients: [],
        clientId: null,
        testOnly: true,
      });
      showToast(`Test email sent to ${currentUser?.email || 'your inbox'}`, 'success');
      onSent && onSent(res);
    } catch (err) {
      showToast(err.message || 'Test send failed', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!validate()) return;
    setSending(true);
    try {
      const res = await bulkSend({
        audience,
        subject: subject.trim(),
        body,
        scope,
        recipients: scope === 'explicit' ? effectiveRecipients : [],
        clientId: scope === 'client' ? clientId : null,
        testOnly: false,
      });
      showToast(`Email sent to ${res.recipientCount} recipient${res.recipientCount === 1 ? '' : 's'}`, 'success');
      setConfirmOpen(false);
      // Soft-reset draft
      setSubject('');
      setBody('');
      setSelectedRecipients([]);
      onSent && onSent(res);
    } catch (err) {
      const more = err.providerError ? ` (${err.providerError})` : '';
      showToast(`Send failed: ${err.message}${more}`, 'error');
    } finally {
      setSending(false);
    }
  };

  const clientOptions = useMemo(() => {
    const list = (clients || []).map((c) => ({
      id: c.id || c._id,
      name: (c.company?.dba || c.company?.legalName || '(unnamed)'),
    }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [clients]);

  return (
    <div data-testid="bulk-email-compose" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
      {/* Compose form */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Audience picker */}
        <div style={S.card}>
          <div style={S.heading}>1 · Audience</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {audienceOptions.map((a) => (
              <button
                key={a.value}
                type="button"
                data-testid={`audience-${a.value}`}
                onClick={() => { setAudience(a.value); setScope('all'); setClientId(''); }}
                style={{
                  flex: '1 1 140px',
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: `1px solid ${audience === a.value ? C.accent : C.border}`,
                  background: audience === a.value ? C.accentBg : '#fff',
                  color: audience === a.value ? C.accent : C.text,
                  fontWeight: audience === a.value ? 700 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  textAlign: 'left',
                  fontFamily: "'Inter', sans-serif",
                  transition: 'background 0.15s, border-color 0.15s',
                }}
              >
                <Icon name={a.icon} style={{ fontSize: 18 }} />
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scope selector */}
        <div style={S.card}>
          <div style={S.heading}>2 · Who Gets It</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ScopeOption
              testId="scope-all"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
              title={`Everyone (${audienceLabel(audience)})`}
              hint={`Send to all ${audienceLabel(audience).toLowerCase()} in your scope`}
            />
            {audience !== 'account_manager' && (
              <ScopeOption
                testId="scope-client"
                checked={scope === 'client'}
                onChange={() => setScope('client')}
                title={`Everyone at one client (${audience === 'employer' ? 'employer contacts' : 'employees'})`}
                hint="Pick one employer below."
              />
            )}
            <ScopeOption
              testId="scope-explicit"
              checked={scope === 'explicit'}
              onChange={() => setScope('explicit')}
              title="Specific individuals only"
              hint="Pick from the list on the right."
            />
            {scope === 'client' && audience !== 'account_manager' && (
              <select
                data-testid="bulk-client-picker"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                style={{ ...S.select, marginTop: 4 }}
              >
                <option value="">— Choose a client —</option>
                {clientOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <div style={{ marginTop: 4, padding: '6px 10px', background: C.bg, borderRadius: 6, fontSize: 12, color: C.textMid, fontFamily: "'JetBrains Mono', monospace" }} data-testid="bulk-recipient-count">
              {poolLoading
                ? 'Loading recipients…'
                : `${effectiveRecipients.length} recipient${effectiveRecipients.length === 1 ? '' : 's'} will receive this email`}
            </div>
          </div>
        </div>

        {/* Subject + body */}
        <div style={S.card}>
          <div style={S.heading}>3 · Compose</div>
          <label style={S.label}>Subject *</label>
          <input
            data-testid="bulk-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Important: new benefits update"
            style={S.input}
          />
          <div style={{ marginTop: 12 }}>
            <label style={S.label}>Body (Markdown supported)</label>
            <textarea
              data-testid="bulk-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder={[
                '# Hello team',
                '',
                'Quick update on the **new benefits enrollment window** opening this week.',
                '',
                '- Window opens Monday',
                '- Deadline is the 30th',
                '- See attached FAQ',
                '',
                'Reach out if you have questions.',
              ].join('\n')}
              style={{ ...S.input, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, resize: 'vertical' }}
            />
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>
              Use <code>**bold**</code>, <code>*italic*</code>, <code># Heading</code>, lists, and{' '}
              <code>[link](https://...)</code>. The email is auto-wrapped in HC branding.
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="bulk-send-test-btn"
            disabled={sending || !subject.trim() || !body.trim()}
            onClick={handleSendTest}
            style={{ ...S.buttonSecondary, opacity: (sending || !subject.trim() || !body.trim()) ? 0.5 : 1 }}
          >
            <Icon name="paper-plane-outline" style={{ fontSize: 14, marginRight: 6, verticalAlign: '-1px' }} />
            Send test to me
          </button>
          <button
            type="button"
            data-testid="bulk-preview-btn"
            onClick={() => setPreviewOpen(true)}
            style={S.buttonSecondary}
          >
            <Icon name="eye-outline" style={{ fontSize: 14, marginRight: 6, verticalAlign: '-1px' }} />
            Preview
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            data-testid="bulk-send-btn"
            disabled={sending || effectiveRecipients.length === 0 || !subject.trim() || !body.trim()}
            onClick={() => { if (validate()) setConfirmOpen(true); }}
            style={{ ...S.buttonPrimary, opacity: (sending || effectiveRecipients.length === 0) ? 0.5 : 1 }}
          >
            <Icon name="send-outline" style={{ fontSize: 14, marginRight: 6, verticalAlign: '-1px' }} />
            Send to {effectiveRecipients.length}
          </button>
        </div>
      </div>

      {/* Right column: recipient picker + live HC preview */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {scope === 'explicit' && (
          <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <div style={S.heading}>Pick recipients ({selectedRecipients.length}/{recipientPool.length})</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  data-testid="recipient-select-filtered"
                  onClick={selectAllFiltered}
                  style={{ ...S.buttonSecondary, padding: '4px 10px', fontSize: 11 }}
                >
                  Select filtered
                </button>
                <button
                  type="button"
                  data-testid="recipient-clear"
                  onClick={clearSelection}
                  style={{ ...S.buttonSecondary, padding: '4px 10px', fontSize: 11 }}
                >
                  Clear
                </button>
              </div>
            </div>
            <input
              data-testid="recipient-search"
              type="text"
              placeholder="Search name, email, or company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...S.input, marginTop: 8 }}
            />
            <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 8, border: `1px solid ${C.borderLight}`, borderRadius: 8 }}>
              {poolLoading && (
                <div style={{ padding: 14, fontSize: 13, color: C.textLight, fontStyle: 'italic' }}>Loading…</div>
              )}
              {!poolLoading && filteredPool.length === 0 && (
                <div style={{ padding: 14, fontSize: 13, color: C.textLight, fontStyle: 'italic' }}>
                  No recipients match.
                </div>
              )}
              {filteredPool.map((r) => {
                const checked = !!selectedRecipients.find((x) => x.email === r.email);
                return (
                  <label
                    key={r.email}
                    data-testid={`recipient-row-${r.email}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      borderBottom: `1px solid ${C.borderLight}`,
                      cursor: 'pointer',
                      background: checked ? C.accentBg : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRecipient(r)}
                      data-testid={`recipient-toggle-${r.email}`}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.name || r.email}
                      </div>
                      <div style={{ fontSize: 11, color: C.textLight, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.email}{r.clientName ? ` · ${r.clientName}` : ''}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Live HC-branded preview */}
        <div style={S.card}>
          <div style={S.heading}>HealthCues preview</div>
          <BrandedPreview subject={subject} body={body} senderName={currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : 'HealthCues Team'} />
        </div>
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <ConfirmModal
          audience={audience}
          recipients={effectiveRecipients}
          subject={subject}
          sending={sending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleConfirmSend}
        />
      )}

      {/* Full-screen preview modal */}
      {previewOpen && (
        <PreviewModal
          subject={subject}
          body={body}
          senderName={currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : 'HealthCues Team'}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

function ScopeOption({ checked, onChange, title, hint, testId }) {
  return (
    <label
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '8px 10px',
        border: `1px solid ${checked ? C.accent : C.border}`,
        borderRadius: 8,
        background: checked ? C.accentBg : '#fff',
        cursor: 'pointer',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <input type="radio" checked={checked} onChange={onChange} style={{ marginTop: 3 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{title}</div>
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{hint}</div>
      </div>
    </label>
  );
}

function BrandedPreview({ subject, body, senderName }) {
  const html = renderMarkdown(body || '_Body will appear here._');
  return (
    <div data-testid="bulk-branded-preview" style={{ border: `1px solid ${C.borderLight}`, borderRadius: 10, overflow: 'hidden', background: '#f8fafc' }}>
      {/* HC header */}
      <div style={{ background: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)', padding: '20px 16px', textAlign: 'center' }}>
        <img
          src="https://customer-assets.emergentagent.com/job_crazy-ellis-3/artifacts/hydsmihy_HealthCues_Workplace_Health_Solutions_Logo_simple.svg"
          alt="HealthCues"
          style={{ height: 32, display: 'inline-block', filter: 'brightness(0) invert(1)' }}
        />
      </div>
      {/* Body */}
      <div style={{ background: '#fff', padding: '20px 18px' }}>
        <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
          Subject
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16 }}>
          {subject || <em style={{ color: C.textLight, fontWeight: 500 }}>(empty subject)</em>}
        </div>
        <div className="md-preview" style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: html }} />
        <div style={{ marginTop: 18, color: C.textMid, fontSize: 13 }}>
          — {senderName}<br />
          <span style={{ color: C.textLight, fontSize: 11 }}>HealthCues Team</span>
        </div>
      </div>
      {/* Footer */}
      <div style={{ padding: '14px 16px', background: '#f8fafc', borderTop: `1px solid ${C.borderLight}`, color: C.textLight, fontSize: 11, textAlign: 'center' }}>
        © 2026 HealthCues. All rights reserved.
      </div>
    </div>
  );
}

function ConfirmModal({ audience, recipients, subject, sending, onCancel, onConfirm }) {
  const sample = recipients.slice(0, 5);
  const more = Math.max(0, recipients.length - sample.length);
  return (
    <Backdrop onClose={onCancel}>
      <div data-testid="bulk-confirm-modal" style={modalCardStyle}>
        <div style={{ ...S.heading, marginBottom: 8 }}>Confirm send</div>
        <div style={{ fontSize: 14, color: C.text, marginBottom: 4 }}>
          You're about to send <strong>{recipients.length} email{recipients.length === 1 ? '' : 's'}</strong> to{' '}
          <strong>{audienceLabel(audience)}</strong>.
        </div>
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>
          Subject: <strong style={{ color: C.text }}>{subject || '(empty)'}</strong>
        </div>
        <div style={{ background: C.bg, borderRadius: 8, padding: 10, fontSize: 12, color: C.textMid, maxHeight: 220, overflowY: 'auto' }}>
          {sample.map((r) => (
            <div key={r.email} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ color: C.text, fontWeight: 500 }}>{r.name || r.email}</span>
              <span style={{ color: C.textLight }}>{r.email}</span>
            </div>
          ))}
          {more > 0 && (
            <div style={{ marginTop: 6, color: C.textLight, fontStyle: 'italic' }}>…and {more} more</div>
          )}
        </div>
        <div style={{ marginTop: 16, padding: 10, borderRadius: 8, background: C.orangeLight, color: C.orange, fontSize: 12, border: `1px solid ${C.orange}33` }}>
          <Icon name="alert-circle-outline" style={{ fontSize: 13, marginRight: 4, verticalAlign: '-2px' }} />
          Emails go out immediately and a copy is stored in the audit log. This can't be undone.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            type="button"
            data-testid="bulk-confirm-cancel"
            onClick={onCancel}
            style={S.buttonSecondary}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="bulk-confirm-send"
            disabled={sending}
            onClick={onConfirm}
            style={{ ...S.buttonPrimary, opacity: sending ? 0.6 : 1 }}
          >
            {sending ? 'Sending…' : `Yes, send to ${recipients.length}`}
          </button>
        </div>
      </div>
    </Backdrop>
  );
}

function PreviewModal({ subject, body, senderName, onClose }) {
  return (
    <Backdrop onClose={onClose}>
      <div data-testid="bulk-preview-modal" style={{ ...modalCardStyle, maxWidth: 720, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={S.heading}>Full preview</div>
          <button type="button" onClick={onClose} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>
            Close
          </button>
        </div>
        <BrandedPreview subject={subject} body={body} senderName={senderName} />
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.4)',
        backdropFilter: 'blur(2px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}

const modalCardStyle = {
  background: '#fff',
  borderRadius: 12,
  padding: 20,
  border: `1px solid ${C.border}`,
  boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
  width: '100%',
  maxWidth: 520,
};
